/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { CurrentData } from '../types';

interface Collection {
  id: string;
  title: string;
  handle: string;
  updated_at: string;
}

interface CollectionContextType {
  userCollections: string[];
  selectedCollectionIndex: number;
  selectedCollection: string | null;
  setSelectedCollectionIndex: (index: number) => void;
  loading: boolean;
  isAdmin: boolean;
  refreshCollections: () => Promise<void>;
  getCollectionTitle: (handle: string) => string;
  allCollectionsMetadata: Collection[];
}

const CollectionContext = createContext<CollectionContextType | undefined>(undefined);

export const CollectionProvider: React.FC<{ 
  children: React.ReactNode;
  currentData?: CurrentData | null;
}> = ({ children, currentData }) => {
  const [userCollections, setUserCollections] = useState<string[]>([]);
  const [selectedCollectionIndex, setSelectedCollectionIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [allCollectionsMetadata, setAllCollectionsMetadata] = useState<Collection[]>([]);
  
  // Track if we've already loaded for this user session - prevents double loading
  const hasLoadedRef = useRef<string | null>(null);

  const isAdmin = currentData?.data?.isAdmin || false;

  // Load all collections metadata from Firestore (for title lookup)
  useEffect(() => {
    const loadCollectionsMetadata = async () => {
      const user = auth.currentUser;
      if (!user) {
        console.log('⚠️ Skipping collections metadata load - no authenticated user');
        return;
      }

      try {
        console.log('📡 Loading collections metadata for title lookup...');
        const collectionsRef = doc(db, 'shopify_metadata', 'collections');
        const collectionsDoc = await getDoc(collectionsRef);
        
        if (collectionsDoc.exists()) {
          const data = collectionsDoc.data();
          const collections = data.collections || [];
          console.log('✅ Loaded collections metadata:', collections.length, 'collections');
          setAllCollectionsMetadata(collections);
        } else {
          console.log('⚠️ No collections metadata found');
          setAllCollectionsMetadata([]);
        }
      } catch (error) {
        console.error('❌ Error loading collections metadata:', error);
        setAllCollectionsMetadata([]);
      }
    };

    loadCollectionsMetadata();
  }, []);

  // Load user collections - only once per user session
  useEffect(() => {
    const loadUserCollections = async () => {
      const user = auth.currentUser;
      if (!user) {
        console.log('⚠️ No current user');
        setLoading(false);
        return;
      }

      // Skip if we already loaded for this user
      const userKey = `${user.uid}-${isAdmin}`;
      if (hasLoadedRef.current === userKey) {
        console.log('⏭️ Already loaded collections for this user, skipping');
        return;
      }

      console.log('🔄 CollectionContext: Loading collections...');
      setLoading(true);
      
      try {
        if (isAdmin) {
          console.log('📡 Admin user - fetching from shopify_metadata/collections');
          const collectionsRef = doc(db, 'shopify_metadata', 'collections');
          const collectionsDoc = await getDoc(collectionsRef);
          
          if (collectionsDoc.exists()) {
            const data = collectionsDoc.data();
            const collectionHandles = (data.collections || []).map((c: any) => c.handle);
            console.log('✅ Loading admin collections (handles) from Firebase:', collectionHandles);
            setUserCollections(collectionHandles);
          } else if (currentData?.data?.tagsArray) {
            console.log('✅ Using tagsArray from currentData:', currentData.data.tagsArray);
            setUserCollections(currentData.data.tagsArray);
          } else {
            setUserCollections([]);
          }
        } else {
          // Non-admin
          if (currentData?.data?.tags) {
            console.log('✅ Loading user collections (handles) from currentData:', currentData.data.tags);
            setUserCollections(currentData.data.tags);
          } else {
            // Fallback: Load from Firestore
            console.log('📡 Loading from Firestore...');
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const handles = userData.collectionHandles || userData.tags || [];
              console.log('✅ Loading user collections (handles) from Firestore:', handles);
              setUserCollections(handles);
            } else {
              setUserCollections([]);
            }
          }
        }
        
        // Mark as loaded for this user
        hasLoadedRef.current = userKey;
        
      } catch (error) {
        console.error('❌ Error loading user collections:', error);
        setUserCollections([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentData) {
      loadUserCollections();
    } else {
      // User logged out - reset the ref
      hasLoadedRef.current = null;
      setUserCollections([]);
      setSelectedCollectionIndex(0);
      setLoading(false);
    }
  }, [currentData, isAdmin]);

  // Refresh collections manually (called after sync) - memoized for the
  // same reason as getCollectionTitle/value below.
  const refreshCollections = useCallback(async () => {
    console.log('🔄 Manually refreshing collections...');

    if (!isAdmin) {
      console.log('⚠️ Not admin - collections refresh not applicable');
      return;
    }

    try {
      const collectionsRef = doc(db, 'shopify_metadata', 'collections');
      const collectionsDoc = await getDoc(collectionsRef);

      if (collectionsDoc.exists()) {
        const data = collectionsDoc.data();
        const collectionHandles = (data.collections || []).map((c: any) => c.handle);
        console.log('✅ Refreshed admin collections (handles):', collectionHandles);
        setUserCollections(collectionHandles);
        setAllCollectionsMetadata(data.collections || []);
      } else {
        console.log('⚠️ No collections found after refresh');
      }
    } catch (error) {
      console.error('❌ Error refreshing collections:', error);
    }
  }, [isAdmin]);

  // Function to get title from handle - memoized so consumers that put it
  // in a dependency array (e.g. a useMemo computing a payout cutoff date)
  // don't recompute on every unrelated render of this provider. Before
  // this, a brand-new function identity on every render cascaded into
  // repeated, expensive refetches downstream - see the dashboard
  // performance notes around useEarnedSincePayout.
  const getCollectionTitle = useCallback((handle: string): string => {
    const collection = allCollectionsMetadata.find(c => c.handle === handle);
    if (collection) {
      return collection.title;
    }
    // Fallback: format the handle nicely if no metadata found
    return handle
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }, [allCollectionsMetadata]);

  const selectedCollection = userCollections[selectedCollectionIndex] || null;

  // Memoized for the same reason as getCollectionTitle above - an inline
  // object literal here would give every consumer of useCollection() a
  // new context value (and therefore a new getCollectionTitle reference
  // even though it's itself memoized) on every render of this provider.
  const value = useMemo(() => ({
    userCollections,
    selectedCollectionIndex,
    selectedCollection,
    setSelectedCollectionIndex,
    loading,
    isAdmin,
    refreshCollections,
    getCollectionTitle,
    allCollectionsMetadata
  }), [
    userCollections,
    selectedCollectionIndex,
    selectedCollection,
    loading,
    isAdmin,
    refreshCollections,
    getCollectionTitle,
    allCollectionsMetadata
  ]);

  return (
    <CollectionContext.Provider value={value}>
      {children}
    </CollectionContext.Provider>
  );
};

export const useCollection = () => {
  const context = useContext(CollectionContext);
  if (context === undefined) {
    throw new Error('useCollection must be used within a CollectionProvider');
  }
  return context;
};